/**
 * DetailHeroScene Styled Components
 * TV-optimized hero section styling for content detail pages.
 * Follows Flutter TV hero pattern (tv_detail_hero.dart).
 * 
 * LEGACY CSS ONLY: No flexbox primary layout, no backdrop-blur.
 */
import styled from 'styled-components';
import { FONT_FAMILY } from '../../config/globalStyles';

// Hero container - 70% viewport height like Flutter
export const HeroContainer = styled.div`
  position: relative;
  width: 100%;
  height: 85vh;
  min-height: 500px;
  overflow: hidden;
`;

// Background image with LQIP support
export const HeroBackground = styled.div`
  position: absolute;
  top: 0;
  left: -1px;
  right: -1px;
  height: 90vh;
  z-index: 1;

  /* LQIP placeholder with blur (shows first) */
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-image: ${props => props.$lqipImage ? `url(${props.$lqipImage})` : 'none'};
    background-size: cover;
    background-position: center;
    background-repeat: no-repeat;
    filter: blur(10px);
    -webkit-filter: blur(10px);
    transform: scale(1.05);
    -webkit-transform: scale(1.05);
    z-index: 0;
    -webkit-transition: opacity 0.3s ease-out;
    transition: opacity 0.3s ease-out;
  }

  /* Main image */
  background-image: url(${props => props.$image});
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
`;

// Vertical overlay - mirrored gradient on left side (like Flutter)
export const VerticalOverlay = styled.div`
  position: absolute;
  top: 0;
  left: -1px;
  width: 50%;
  height: 85vh;
  background-image: url(/shade-v.png);
  background-position: right;
  background-size: cover;
  background-repeat: no-repeat;
  z-index: 2;
  -webkit-transform: scaleX(-1);
  -moz-transform: scaleX(-1);
  -o-transform: scaleX(-1);
  transform: scaleX(-1);
`;

// Horizontal overlay - bottom gradient fade
export const HorizontalOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: -4px;
  background-image: url(/shade-h.svg);
  background-position: bottom;
  background-size: contain;
  background-repeat: no-repeat;
  z-index: 3;
`;

// Content wrapper - positioned over overlays
export const ContentWrapper = styled.div`
  position: absolute;
  top: 50px;
  right: 5%;
  bottom: 7.5%;
  left: 5%;
  z-index: 10;
`;

// Content inner - uses table layout for legacy TV compatibility
export const ContentInner = styled.div`
  display: table;
  width: 100%;
  height: 100%;
`;

export const ContentCell = styled.div`
  display: table-cell;
  vertical-align: bottom;
  width: 60%;
`;

// Text shadow mixin values (matching Flutter)
const TEXT_SHADOW = `
  0 1px 2px rgba(0, 0, 0, 1),
  0 2px 8px rgba(0, 0, 0, 1),
  0 0 20px rgba(0, 0, 0, 0.54)
`;

const TEXT_SHADOW_LIGHT = `
  0 1px 2px rgba(0, 0, 0, 1),
  0 2px 8px rgba(0, 0, 0, 1),
  0 0 16px rgba(0, 0, 0, 0.54)
`;

// Title text - large bold with shadows
export const TitleText = styled.h1`
  font-family: ${FONT_FAMILY};
  font-size: 40px;
  font-weight: 700;
  color: #FFFFFF;
  letter-spacing: 0;
  margin: 0 0 8px 0;
  text-shadow: ${TEXT_SHADOW};
`;

// English title - medium with shadows
export const SubtitleText = styled.h2`
  font-family: ${FONT_FAMILY};
  font-size: 30px;
  font-weight: 700;
  color: #FFFFFF;
  letter-spacing: 0;
  margin: 0 0 10px 0;
  text-shadow: ${TEXT_SHADOW};
`;

// Metadata row container
export const MetadataRow = styled.div`
  margin: 10px 0;
`;

// Metadata item (inline-block for legacy support)
export const MetadataItem = styled.span`
  display: inline-block;
  vertical-align: middle;
  font-family: ${FONT_FAMILY};
  font-size: 24px;
  font-weight: 700;
  color: #FFFFFF;
  text-shadow: ${TEXT_SHADOW_LIGHT};
  margin-left: 20px;

  &:first-child {
    margin-left: 0;
  }
`;

// Duration icon wrapper
export const DurationWrapper = styled.span`
  display: inline-block;
  vertical-align: middle;
  margin-right: 20px;
`;

export const ClockIcon = styled.span`
  display: inline-block;
  vertical-align: middle;
  margin-right: 5px;
  color: #FFFFFF;
  font-size: 20px;
  text-shadow: 0 1px 2px rgba(0,0,0,1), 0 0 12px rgba(0,0,0,1);
`;

// Age badge wrapper
export const AgeBadgeWrapper = styled.span`
  display: inline-block;
  vertical-align: middle;
  margin-right: 20px;
  filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.8));
`;

// IMDB and rating row
export const RatingRow = styled.div`
  margin: 10px 0;
`;

export const RatingItem = styled.span`
  display: inline-block;
  vertical-align: middle;
  margin-right: 20px;

  &:first-child {
    margin-right: 0;
  }
`;

export const ImdbLogo = styled.img`
  display: inline-block;
  vertical-align: middle;
  width: 50px;
  height: auto;
  margin-left: 8px;
  filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.8));
`;

export const LikeIcon = styled.img`
  display: inline-block;
  vertical-align: middle;
  width: 22px;
  height: auto;
  margin-left: 8px;
  filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.8));
`;

export const RatingText = styled.span`
  display: inline-block;
  vertical-align: middle;
  font-family: Arial;
  font-size: 24px;
  font-weight: 600;
  color: #FFFFFF;
  text-shadow: ${TEXT_SHADOW_LIGHT};
`;

// Description text
export const DescriptionText = styled.p`
  font-family: ${FONT_FAMILY};
  font-size: 22px;
  font-weight: 400;
  color: #FFFFFF;
  letter-spacing: 0;
  line-height: 1.5;
  margin: 40px 0;
  max-width: 50%;
  text-shadow: ${TEXT_SHADOW_LIGHT};
`;

// Genre chips container
export const GenreChipsContainer = styled.div`
  margin: 12px 0;
  display: flex;
  flex-wrap: wrap;
`;

// Genre chip button - non-blur glassmorphism
export const GenreChip = styled.button`
  display: inline-block;
  vertical-align: middle;
  font-family: ${FONT_FAMILY};
  font-size: 28px;
  font-weight: 600;
  color: ${props => props.$focused ? '#333' : '#F0F0F0'};
  background: ${props => props.$focused ? 'rgba(255,255,255,0.95)' : 'rgba(50, 50, 50)'};
  border-radius: 15px;
  padding: 10px 24px;
  cursor: pointer;
  border: ${props => props.$focused ? '2px solid #FFF' : '2px solid rgba(255,255,255,0.2)'};
  box-shadow: 0 4px 16px rgba(0,0,0,0.4);
  text-shadow: ${props => props.$focused ? 'none' : '0 1px 2px rgba(0,0,0,0.8)'};
  -webkit-transition: all 0.15s ease;
  transition: all 0.15s ease;
  margin-left: 15px;
  &:hover {
    background: rgba(255,255,255,0.95);
    color: #333;
    text-shadow: none;
  }
`;

// Action buttons row
export const ActionButtonsRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 10px;
  margin-top: 20px;
  width: 100%;
`;

// Video logo container - positioned bottom left of hero
export const VideoLogoContainer = styled.div`
  position: absolute;
  left: 52px;
  bottom: 7.5rem;
  z-index: 15;
`;

export const VideoLogo = styled.img`
  width: 125px;
  height: auto;
  object-fit: contain;
`;

// Turkish badge
export const TurkishBadge = styled.img`
  display: block;
  width: 200px;
  height: auto;
  margin-bottom: 30px;
`;

// Spacer for buttons
export const ButtonSpacer = styled.span`
  display: inline-block;
  width: ${props => props.$width || '10px'};
`;

// Right side actions container (like, dislike, bookmark)
export const IconActionsContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
`;
